import { resolveRoomObjectImageSrcForDisplay } from '../roomObjectImage';
import { resolvePlacementImageSrc } from './roomObjectThumbnails';

function placementHeightPx(item) {
  const w = Number(item?.width) || 48;
  const ar = Number(item?.aspectRatio) > 0 ? Number(item.aspectRatio) : 1;
  return w / ar;
}

/** Center-based placement → top-left draw rect in image pixel space (matches overlay layout). */
export function getPlacementDrawRect(placement) {
  const width = Math.max(24, Number(placement?.width) || 48);
  const height = placementHeightPx(placement);
  const cx = Number(placement?.x) || 0;
  const cy = Number(placement?.y) || 0;
  return {
    x: cx - width / 2,
    y: cy - height / 2,
    width,
    height,
  };
}

/** Mimics CSS `object-fit: contain` inside a box. */
export function drawImageObjectContain(ctx, img, boxX, boxY, boxW, boxH) {
  const imgW = Number(img?.naturalWidth) || Number(img?.width) || 0;
  const imgH = Number(img?.naturalHeight) || Number(img?.height) || 0;
  if (!(imgW > 0) || !(imgH > 0) || !(boxW > 0) || !(boxH > 0)) return;

  const scale = Math.min(boxW / imgW, boxH / imgH);
  const drawW = imgW * scale;
  const drawH = imgH * scale;
  const drawX = boxX + (boxW - drawW) / 2;
  const drawY = boxY + (boxH - drawH) / 2;
  ctx.drawImage(img, drawX, drawY, drawW, drawH);
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const url = typeof src === 'string' ? src.trim() : '';
    if (!url) {
      reject(new Error('Missing image URL'));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

async function resolveBaseImage({ baseImage, baseImageSrc }) {
  if (baseImage instanceof HTMLImageElement) {
    if (baseImage.complete && (baseImage.naturalWidth > 0 || baseImage.width > 0)) {
      return baseImage;
    }
    const src = baseImageSrc || baseImage.currentSrc || baseImage.src;
    if (src) return loadImageElement(src);
  }
  if (typeof baseImageSrc === 'string' && baseImageSrc.trim()) {
    return loadImageElement(baseImageSrc);
  }
  throw new Error('No base image available for composite.');
}

async function loadPlacementImage(placement, roomId) {
  const src = await resolvePlacementImageSrc(placement, roomId);
  if (!src) return null;
  try {
    return await loadImageElement(src);
  } catch (err) {
    console.warn('Skipping placement image during composite:', placement?.placementId, err);
    return null;
  }
}

/**
 * Flatten the active canvas image and all curated model placements into one raster image.
 * Placements use the same center position, width, and aspect ratio as on-canvas overlays.
 *
 * @param {{
 *   baseImage?: HTMLImageElement | null,
 *   baseImageSrc?: string | null,
 *   width: number,
 *   height: number,
 *   placements?: Array<{
 *     placementId?: string,
 *     roomObjectId?: number | string,
 *     thumbSrc?: string,
 *     x: number,
 *     y: number,
 *     width: number,
 *     aspectRatio?: number,
 *   }>,
 *   roomId?: number | string | null,
 *   mimeType?: string,
 *   quality?: number,
 * }} options
 * @returns {Promise<{ canvas: HTMLCanvasElement, dataUrl: string, width: number, height: number }>}
 */
export async function combineCanvasWithCuratedModels({
  baseImage = null,
  baseImageSrc = null,
  width,
  height,
  placements = [],
  roomId = null,
  mimeType = 'image/png',
  quality,
}) {
  const logicalW = Math.round(Number(width) || 0);
  const logicalH = Math.round(Number(height) || 0);
  if (!(logicalW > 0) || !(logicalH > 0)) {
    throw new Error('Invalid canvas dimensions for composite.');
  }

  const base = await resolveBaseImage({ baseImage, baseImageSrc });
  const canvas = document.createElement('canvas');
  canvas.width = logicalW;
  canvas.height = logicalH;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create canvas context for composite.');
  }

  ctx.drawImage(base, 0, 0, logicalW, logicalH);

  const orderedPlacements = Array.isArray(placements) ? placements : [];
  const failedPlacements = [];
  for (const placement of orderedPlacements) {
    const overlayImg = await loadPlacementImage(placement, roomId);
    if (!overlayImg) {
      if (placement?.roomObjectId != null) {
        failedPlacements.push(placement);
      }
      continue;
    }
    const rect = getPlacementDrawRect(placement);
    drawImageObjectContain(ctx, overlayImg, rect.x, rect.y, rect.width, rect.height);
  }

  if (failedPlacements.length > 0) {
    throw new Error(
      `Could not load ${failedPlacements.length} model image${failedPlacements.length > 1 ? 's' : ''} for combine. Please try again.`,
    );
  }

  const dataUrl =
    mimeType === 'image/jpeg' || mimeType === 'image/webp'
      ? canvas.toDataURL(mimeType, quality)
      : canvas.toDataURL(mimeType);

  return {
    canvas,
    dataUrl,
    width: logicalW,
    height: logicalH,
  };
}

/** Export combined canvas raster as a PNG File for multipart upload. */
export function canvasToPngFile(canvas, filename = 'combined-image.png') {
  return new Promise((resolve, reject) => {
    if (!(canvas instanceof HTMLCanvasElement)) {
      reject(new Error('Invalid canvas for PNG export.'));
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to export combined image.'));
        return;
      }
      resolve(new File([blob], filename, { type: 'image/png' }));
    }, 'image/png');
  });
}
